import { PropsWithChildren, useEffect, useRef } from 'react';
import { LocalizationContextBuilder } from '@amzn/arb-tools';
import { MbmProvider, useLocalizationContext } from '@amzn/react-arb-tools';
import arbManifest from '@amzn/red-velvet-translations/dist/arbManifest.json';
import { useSearchParams } from 'react-router-dom';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { useMessages } from '../../hooks/messages';
import { useGetUserPreference } from '../../hooks/userPreferences';
import { DEFAULT_LOCALE } from '../../data/supportedLanguages';
import {
  LOCALIZATION_OFF_LOCALE,
  browserLanguages,
  primaryBrowserLanguage,
  resolveSessionLocale,
} from '../../utils/localeUtils';
import {
  LOCALIZATION,
  SESSION_BROWSER_LOCALE_METRIC,
  SESSION_LOCALE_METRIC,
  SESSION_LOCALE_SOURCE_METRIC,
  WEBSITE_COMMON_BUNDLE,
} from '../../utils/constants';
import { publishKatalMetric } from '../katalAnalytics';
import { CloudscapeI18nProvider } from './cloudscapeI18nProvider';

/** Where the deployed site serves the language packs from. */
export const TRANSLATIONS_URL_PREFIX = '/translations';

const mbmOptions = {
  arbManifest,
  defaultLocalizationContext: new LocalizationContextBuilder()
    .withLocale(DEFAULT_LOCALE)
    .withDefaultLocale(DEFAULT_LOCALE)
    .build(),
  urlPrefix: TRANSLATIONS_URL_PREFIX,
};

/**
 * Serves the site's text in the user's language.
 *
 * Renders straight away in English rather than holding the page back while the user's language is
 * looked up, so a slow lookup costs nobody the site. Text switches over once the language is known.
 */
export function LocalizationProvider({ children }: PropsWithChildren) {
  return (
    <MbmProvider {...mbmOptions}>
      <SessionLocale />
      <CloudscapeI18nProvider>{children}</CloudscapeI18nProvider>
    </MbmProvider>
  );
}

function SessionLocale() {
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const localizationEnabled = featureIsEnabled(LOCALIZATION, features, searchParams);
  const preferenceQuery = useGetUserPreference(localizationEnabled);
  const { localizationContext, setLocalizationContext } = useLocalizationContext();
  const reportedLocale = useRef(false);

  const { locale: resolvedLocale, source: localeSource } = localizationEnabled
    ? resolveSessionLocale(
        preferenceQuery.data?.locale,
        preferenceQuery.data?.localeSource,
        browserLanguages()
      )
    : LOCALIZATION_OFF_LOCALE;
  // Their saved language wins over their browser's, so the site stays in English until the saved one
  // is known rather than switching twice. A language chosen earlier this session is known already.
  const localeIsFinal = !localizationEnabled || !preferenceQuery.isPending;

  useEffect(() => {
    if (!localeIsFinal || resolvedLocale === localizationContext.getLocale()) {
      return;
    }
    setLocalizationContext(
      new LocalizationContextBuilder()
        .withLocale(resolvedLocale)
        .withDefaultLocale(DEFAULT_LOCALE)
        .build()
    );
  }, [localeIsFinal, resolvedLocale, localizationContext, setLocalizationContext]);

  // Reported once per page load. Changing language reloads the page, and the analytics session id
  // outlives that reload, so a session where someone switches reports twice: once for the language
  // they arrived in, once for the one they chose. Reading these per session means taking the last
  // value rather than expecting one row each.
  useEffect(() => {
    if (!localeIsFinal || reportedLocale.current) {
      return;
    }
    reportedLocale.current = true;
    publishKatalMetric(SESSION_LOCALE_METRIC, resolvedLocale);
    publishKatalMetric(SESSION_LOCALE_SOURCE_METRIC, localeSource);
    // Reported even when the user saved a language, so we can see how often a saved choice disagrees
    // with the browser. Skipped when the browser names none, which would report an empty language.
    const browserLanguage = primaryBrowserLanguage();
    if (browserLanguage) {
      publishKatalMetric(SESSION_BROWSER_LOCALE_METRIC, browserLanguage);
    }
  }, [localeIsFinal, resolvedLocale, localeSource]);

  // Tells the page what language it is in, which two things read and nothing else sets.
  //
  // A screen reader picks its pronunciation from here, so French text under `lang="en"` is read with
  // English phonetics. Cloudscape also resolves the language it formats dates in from this attribute:
  // it is given no locale of its own, and falls back to the reader's browser language, so a calendar
  // would keep its English month and weekday names, and start its weeks on the wrong day.
  useEffect(() => {
    if (!localeIsFinal) {
      return;
    }
    document.documentElement.lang = resolvedLocale;
  }, [localeIsFinal, resolvedLocale]);

  return localizationEnabled ? <WarmSharedMessages /> : null;
}

/** Fetches the pack the site's own text comes from, so no panel or page has to wait on it. */
function WarmSharedMessages() {
  useMessages(WEBSITE_COMMON_BUNDLE);
  return null;
}
